import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { ensurePortalRoot, type Placement } from '../shared/index.js';
import { useFloating } from '../../hooks/useFloating.js';
import { useEscapeStack } from '../../hooks/useEscapeStack.js';

export interface TooltipProps {
  /** The anchor element. Must be a single React element that can receive a ref. */
  children: ReactElement<HTMLAttributes<HTMLElement> & { ref?: Ref<HTMLElement> }>;
  /** Short label — shown as the tooltip body (or title row in rich mode). */
  label?: ReactNode;
  /** Rich mode: longer explanation under the title. */
  description?: ReactNode;
  /** Rich mode: optional icon to the left of the title (e.g. Kbd hint glyph). */
  icon?: ReactNode;
  /** Rich mode: optional shortcut slot (<Kbd>⌘K</Kbd>). */
  shortcut?: ReactNode;
  placement?: Placement;
  /** Delay before showing, ms. */
  openDelay?: number;
  /** Delay before hiding, ms. */
  closeDelay?: number;
  /** Render a small triangle pointing at the anchor. */
  arrow?: boolean;
  /** Controlled visibility. Uncontrolled if omitted. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When false, tooltip is suppressed entirely (hover still works on the child). */
  disabled?: boolean;
  className?: string;
  id?: string;
}

/** Accessible tooltip. Triggered by hover + focus-visible, suppressed on touch
 *  devices (hover: none) to avoid double-tap-to-close quirks. Content is
 *  announced via `aria-describedby` when the trigger is focused.
 *
 *  Positioning is delegated to `useFloating` — no inline rect / scroll /
 *  resize bookkeeping. */
export function Tooltip(props: TooltipProps): ReactNode {
  const {
    children,
    label,
    description,
    icon,
    shortcut,
    placement = 'top',
    openDelay = 140,
    closeDelay = 80,
    arrow = false,
    open: controlledOpen,
    onOpenChange,
    disabled = false,
    className,
    id,
  } = props;

  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = (isControlled ? controlledOpen : uncontrolledOpen) && !disabled;

  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const reactId = useId();
  const tipId = id ?? `${reactId}-tooltip`;
  const isRich = description !== undefined || shortcut !== undefined || icon !== undefined;

  const { setAnchor, setFloating, rect, floatingStyle, arrowStyle } = useFloating({
    open,
    placement,
  });

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const clearTimers = useCallback(() => {
    if (openTimer.current !== null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    clearTimers();
    openTimer.current = window.setTimeout(() => {
      setOpen(true);
    }, openDelay);
  }, [clearTimers, openDelay, setOpen]);

  const scheduleClose = useCallback(() => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => {
      setOpen(false);
    }, closeDelay);
  }, [clearTimers, closeDelay, setOpen]);

  useEffect(
    () => () => {
      clearTimers();
    },
    [clearTimers],
  );

  // Dismiss on Escape while shown — matches APG guidance. Routed via the
  // shared modal stack so a Tooltip on top of a Dialog dismisses the
  // Tooltip first; a second Esc closes the Dialog.
  useEscapeStack(
    useCallback(() => {
      clearTimers();
      setOpen(false);
    }, [clearTimers, setOpen]),
    open,
  );

  const child = Children.only(children);
  if (!isValidElement(child)) return child;

  const prevRef = (child as ReactElement<{ ref?: Ref<HTMLElement> }>).props.ref;
  const mergedAnchorRef = (node: HTMLElement | null): void => {
    setAnchor(node);
    if (typeof prevRef === 'function') prevRef(node);
    else if (prevRef !== undefined && prevRef !== null) {
      (prevRef as { current: HTMLElement | null }).current = node;
    }
  };

  const existing = child.props;
  const describedBy =
    [existing['aria-describedby'], open ? tipId : undefined]
      .filter((x): x is string => typeof x === 'string')
      .join(' ') || undefined;

  const triggerProps: HTMLAttributes<HTMLElement> & { ref: Ref<HTMLElement> } = {
    ref: mergedAnchorRef,
    onMouseEnter: (e) => {
      existing.onMouseEnter?.(e);
      scheduleOpen();
    },
    onMouseLeave: (e) => {
      existing.onMouseLeave?.(e);
      scheduleClose();
    },
    onFocus: (e) => {
      existing.onFocus?.(e);
      scheduleOpen();
    },
    onBlur: (e) => {
      existing.onBlur?.(e);
      scheduleClose();
    },
    'aria-describedby': describedBy,
  };

  const portalRoot = ensurePortalRoot();
  const content = label;
  const suppress = disabled || (content === undefined && !isRich);
  if (suppress) return cloneElement(child, { ref: mergedAnchorRef });

  const panelClasses = ['vq-tooltip', className].filter(Boolean).join(' ');

  return (
    <>
      {cloneElement(child, triggerProps)}
      {open && portalRoot !== null
        ? createPortal(
            <div
              ref={setFloating}
              id={tipId}
              role="tooltip"
              className={panelClasses}
              data-component="Tooltip"
              data-variant={isRich ? 'rich' : 'plain'}
              data-side={rect?.side ?? 'top'}
              style={floatingStyle}
              onMouseEnter={isRich ? clearTimers : undefined}
              onMouseLeave={isRich ? scheduleClose : undefined}
            >
              {isRich ? (
                <>
                  <div className="vq-tooltip__rich-head">
                    {icon !== undefined ? (
                      <span className="vq-tooltip__rich-icon" aria-hidden="true">
                        {icon}
                      </span>
                    ) : null}
                    <div className="vq-tooltip__rich-head-text">
                      {label !== undefined ? (
                        <div className="vq-tooltip__title">{label}</div>
                      ) : null}
                      {description !== undefined ? (
                        <div className="vq-tooltip__desc">{description}</div>
                      ) : null}
                    </div>
                  </div>
                  {shortcut !== undefined ? (
                    <div className="vq-tooltip__kbd-row">{shortcut}</div>
                  ) : null}
                </>
              ) : (
                label
              )}
              {arrow ? (
                <span className="vq-tooltip__arrow" aria-hidden="true" style={arrowStyle} />
              ) : null}
            </div>,
            portalRoot,
          )
        : null}
    </>
  );
}
